import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { leaveApi } from "../../services/leaveApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { ReasonDialog } from "../../components/ReasonDialog";
import { Button } from "../../components/ui/button";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

const STATUS_FILTERS = ["PENDING", "APPROVED", "REJECTED", "CANCELLED", ""];

export default function LeaveReviewPage() {
  const { t } = useLocalization();
  const { canViewLeave, canManageLeave } = usePermissions();
  const [requests, setRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [dialog, setDialog] = useState(null); // { kind: "reject"|"cancel", id }

  const load = () => {
    leaveApi.listRequests(undefined, statusFilter || undefined).then((r) => setRequests(r.data)).catch(() => {});
    leaveApi.listEmployeesForLeave().then((r) => setEmployees(r.data)).catch(() => {});
  };

  useEffect(() => { if (canViewLeave) load(); }, [canViewLeave, statusFilter]);

  if (!canViewLeave) return <p className="text-sm text-gray-500" data-testid="leave-review-access-denied">{t("mgmtLeave.accessDenied")}</p>;

  const employeeName = (id) => employees.find((e) => e.id === id)?.legal_name || id;

  const approve = async (id) => {
    try { await leaveApi.approveRequest(id, "Approved"); toast.success(t("mgmtLeave.approveSuccess")); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openDialog = (kind, r) => setDialog({
    kind, id: r.id,
    contextItems: [
      { label: t("mgmtLeave.dialogEmployee"), value: employeeName(r.employee_id) },
      { label: t("mgmtLeave.dialogDates"), value: `${r.start_date} → ${r.end_date}` },
      { label: t("mgmtLeave.dialogDaysRequested"), value: r.days_requested },
      { label: t("mgmtLeave.dialogStatedReason"), value: r.reason || "-" },
      { label: t("mgmtLeave.dialogCurrentStatus"), value: t(`statuses.${r.status}`) },
    ],
  });

  const confirmDialog = async (reason) => {
    if (dialog.kind === "reject") { await leaveApi.rejectRequest(dialog.id, reason); toast.success(t("mgmtLeave.rejectSuccess")); }
    if (dialog.kind === "cancel") { await leaveApi.cancelRequest(dialog.id, reason); toast.success(t("mgmtLeave.cancelSuccess")); }
    load();
  };

  return (
    <div className="space-y-5" data-testid="leave-review-page">
      <PageHelpBanner pageKey="leave-review" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtLeave.reviewHelp") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtLeave.reviewTitle")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtLeave.reviewSubtitle")}</p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {STATUS_FILTERS.map((s) => (
          <button key={s || "ALL"} data-testid={`leave-request-filter-${s || "ALL"}`} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${statusFilter === s ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            {s ? t(`statuses.${s}`) : t("common.all")}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtLeave.colEmployee")}</th><th className="px-4 py-3 font-medium">{t("mgmtLeave.colDates")}</th><th className="px-4 py-3 font-medium">{t("mgmtLeave.colDays")}</th><th className="px-4 py-3 font-medium">{t("mgmtLeave.colReason")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3 font-medium"></th></tr></thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} className="border-t border-gray-100" data-testid={`leave-request-row-${r.id}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">{employeeName(r.employee_id)}</td>
                <td className="px-4 py-2 text-gray-600">{r.start_date} → {r.end_date}</td>
                <td className="px-4 py-2 text-gray-600">{r.days_requested}</td>
                <td className="px-4 py-2 text-gray-600">{r.reason || "-"}</td>
                <td className="px-4 py-2"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-2 space-x-1">
                  {canManageLeave && r.status === "PENDING" && (
                    <>
                      <Button size="sm" data-testid={`leave-approve-btn-${r.id}`} onClick={() => approve(r.id)} className="bg-emerald-600 hover:bg-emerald-700">{t("mgmtLeave.approve")}</Button>
                      <Button size="sm" variant="outline" data-testid={`leave-reject-btn-${r.id}`} onClick={() => openDialog("reject", r)} className="text-red-600 border-red-200 hover:bg-red-50">{t("mgmtLeave.reject")}</Button>
                    </>
                  )}
                  {canManageLeave && r.status === "APPROVED" && (
                    <Button size="sm" variant="outline" data-testid={`leave-cancel-btn-${r.id}`} onClick={() => openDialog("cancel", r)}>{t("mgmtLeave.cancel")}</Button>
                  )}
                </td>
              </tr>
            ))}
            {requests.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500" data-testid="no-leave-requests-row">{t("mgmtLeave.noRequests")}</td></tr>}
          </tbody>
        </table>
      </div>

      {dialog && (
        <ReasonDialog
          open onOpenChange={(v) => !v && setDialog(null)}
          title={dialog.kind === "reject" ? t("mgmtLeave.rejectTitle") : t("mgmtLeave.cancelTitle")}
          contextItems={dialog.contextItems} reasonLabel={dialog.kind === "reject" ? t("mgmtLeave.rejectionReason") : t("mgmtLeave.cancellationReason")}
          danger confirmLabel={dialog.kind === "reject" ? t("mgmtLeave.rejectConfirm") : t("mgmtLeave.cancelConfirm")}
          onConfirm={confirmDialog} testId="leave-request-dialog"
        />
      )}
    </div>
  );
}

